import { scan } from 'rxjs/operators';
import { getLoadedScenarioData } from 'loadedScenarioData';
import { combineLatest, type Observable } from 'rxjs';
import { type PayloadUpdateScopeDiff } from '@ardoq/data-model';
import { DiffMode } from '@ardoq/api-types';
import {
  addComponentsAction,
  mergeComponentsAction,
  resetComponentsAction,
} from 'streams/components/ComponentActions';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  addReferences,
  resetReferences,
} from 'streams/references/ReferenceActions';
import { activeDiffMode$ } from './activeDiffMode$';
import { setDefaultCollectionViews } from './routines';
import { diffModeUpdated } from './actions';
import { contextInterface } from '../modelInterface/contextInterface';
import {
  clearComponent,
  setComponentId,
} from '../streams/context/ContextActions';
import { componentInterface } from '@ardoq/component-interface';
import { resetGraph } from '../modelInterface/graphModelActions';

export const setDiffRoutine = (
  scopeDiff$: Observable<PayloadUpdateScopeDiff>
) => {
  return combineLatest([scopeDiff$, activeDiffMode$]).pipe(
    scan((wasInScopeDiffMode, [{ scopeDiff }, activeDiffMode]) => {
      if (scopeDiff) {
        const components =
          activeDiffMode === DiffMode.SCENARIO
            ? scopeDiff.branchComponents
            : activeDiffMode === DiffMode.DIFF
              ? scopeDiff.diffComponents
              : scopeDiff.masterComponents;

        const references =
          activeDiffMode === DiffMode.SCENARIO
            ? scopeDiff.branchReferences
            : activeDiffMode === DiffMode.DIFF
              ? scopeDiff.diffReferences
              : scopeDiff.masterReferences;

        dispatchAction(mergeComponentsAction({ components }));
        dispatchAction(addReferences({ references }));

        dispatchAction(diffModeUpdated({ activeDiffMode }));
        return true;
      }

      if (wasInScopeDiffMode) {
        dispatchAction(resetReferences());
        dispatchAction(resetComponentsAction());
        dispatchAction(resetGraph());
        const loadedScenarioData = getLoadedScenarioData();
        if (loadedScenarioData) {
          const { components, references, connected } = loadedScenarioData;

          dispatchAction(
            addComponentsAction({
              components: [...components, ...connected.components],
            })
          );

          dispatchAction(
            addReferences({
              references: [...references, ...connected.references],
            })
          );

          setDefaultCollectionViews(loadedScenarioData);

          const selectedComponentId = contextInterface.getCurrentComponentId();
          if (selectedComponentId) {
            // This is not necessarly the same object as `Context.component()`.
            // In visual diff mode we maintain sets of three branches,
            // mainline, branch and branch-off, it could be an object of any
            // of them.
            if (componentInterface.isComponent(selectedComponentId)) {
              dispatchAction(setComponentId(selectedComponentId));
            } else {
              dispatchAction(clearComponent());
            }
          }
        }
      }

      dispatchAction(diffModeUpdated({ activeDiffMode }));
      return false;
    }, false)
  );
};
